import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articleMap, getRelatedArticles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">文章未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
          <Link
            to="/"
            className="inline-flex px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const cat = categoryMap[article.category];
  const related = getRelatedArticles(article.slug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '亚洲一区',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://khwlkj.cn/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <article className="max-w-4xl mx-auto px-4 md:px-6 py-10">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: cat?.name || article.category, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>

            {cat && (
              <Link
                to={`/category/${article.category}`}
                className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors mb-4 whitespace-nowrap cursor-pointer"
              >
                {cat.name}
              </Link>
            )}

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-400 mb-8">
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.author}</span>
            </div>

            <div className="w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          {related.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-14">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.map((r) => (
                  <ArticleCard key={r.slug} {...r} />
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}